import {ReactNode} from 'react';
import {Table} from '@goproblems/ui';
import {Link} from 'react-router';
import {ScenarioCase} from '@api';
import {ScenarioCaseStatusBadge} from './ScenarioCaseStatusBadge';
import {ScenarioEndnessTags} from './ScenarioEndnessTags';

type ScenarioCasesTableProps = {
    cases: ScenarioCase[];
    emptyMessage: string;
    maxHeight?: string;
    actionHeader?: string;
    renderActions?: (caseData: ScenarioCase) => ReactNode;
};

export const scenarioCaseMatchesQuery = (
    caseData: ScenarioCase,
    query: string
) => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) return true;

    const expectedFields = caseData.expectations
        .flatMap(e => Object.keys(e.expected))
        .join(' ');
    const tagCodes = (caseData.tags ?? []).map(tag => tag.code).join(' ');
    const haystack = [
        String(caseData.id),
        caseData.path,
        caseData.description ?? '',
        caseData.difficulty ?? '',
        caseData.isTestCase ? 'test case testcase yes' : 'non test no',
        caseData.isActive ? 'active enabled' : 'inactive disabled',
        caseData.lastExecution?.status ?? 'not executed',
        expectedFields,
        tagCodes,
    ]
        .join(' ')
        .toLowerCase();

    return haystack.includes(normalizedQuery);
};

export const ScenarioCasesTable = ({
    cases,
    emptyMessage,
    maxHeight = 'calc(100vh - 300px)',
    actionHeader = 'Action',
    renderActions = defaultActions,
}: ScenarioCasesTableProps) => (
    <div className="overflow-auto" style={{maxHeight}}>
        <Table striped hover responsive className="mb-0 align-middle">
            <thead className="sticky-top bg-body" style={{zIndex: 1}}>
                <tr>
                    <th style={{width: 92}}>Case</th>
                    <th>Path</th>
                    <th style={{width: 120}}>Difficulty</th>
                    <th style={{width: 112}}>Test Case</th>
                    <th style={{width: 96}}>Active</th>
                    <th style={{width: 132}}>Status</th>
                    <th style={{width: 190}}>Endness</th>
                    <th>Description</th>
                    <th style={{width: 120}}>Checks</th>
                    <th className="text-end" style={{width: 120}}>
                        {actionHeader}
                    </th>
                </tr>
            </thead>
            <tbody>
                {cases.map(c => (
                    <tr key={c.id}>
                        <td>
                            <Link
                                to={`/scenario-cases/${c.id}`}
                                className="text-decoration-none fw-semibold">
                                #{c.id}
                            </Link>
                        </td>
                        <td>
                            <code className="small text-break">
                                {c.path || '<root>'}
                            </code>
                        </td>
                        <td>{c.difficulty || '—'}</td>
                        <td>
                            <TestCaseFlag caseData={c} />
                        </td>
                        <td>
                            <ActiveFlag caseData={c} />
                        </td>
                        <td>
                            <CaseStatus caseData={c} />
                        </td>
                        <td>
                            <ScenarioEndnessTags
                                tags={c.tags}
                                empty={
                                    <span className="text-muted small">—</span>
                                }
                            />
                        </td>
                        <td className="text-truncate" style={{maxWidth: 260}}>
                            {c.description || '—'}
                        </td>
                        <td>
                            {c.expectations.length} expectation
                            {c.expectations.length !== 1 ? 's' : ''}
                        </td>
                        <td className="text-end text-nowrap">
                            {renderActions(c)}
                        </td>
                    </tr>
                ))}
                {cases.length === 0 && (
                    <tr>
                        <td
                            colSpan={10}
                            className="text-center text-muted py-4">
                            {emptyMessage}
                        </td>
                    </tr>
                )}
            </tbody>
        </Table>
    </div>
);

const TestCaseFlag = ({caseData}: {caseData: ScenarioCase}) => {
    if (caseData.isTestCase) {
        return (
            <span className="badge rounded-pill bg-primary-subtle text-primary-emphasis">
                Yes
            </span>
        );
    }

    return (
        <span className="badge rounded-pill bg-body-secondary text-body">
            No
        </span>
    );
};

const ActiveFlag = ({caseData}: {caseData: ScenarioCase}) => {
    if (caseData.isActive) {
        return (
            <span className="badge rounded-pill bg-success-subtle text-success-emphasis">
                Yes
            </span>
        );
    }

    return (
        <span className="badge rounded-pill bg-warning-subtle text-warning-emphasis">
            No
        </span>
    );
};

const CaseStatus = ({caseData}: {caseData: ScenarioCase}) => {
    const execution = caseData.lastExecution;

    if (!execution) {
        return (
            <span className="badge rounded-pill bg-body-secondary text-body">
                Not executed
            </span>
        );
    }

    return <ScenarioCaseStatusBadge status={execution.status} />;
};

const defaultActions = (caseData: ScenarioCase) => (
    <Link
        to={`/scenario-cases/${caseData.id}`}
        className="btn btn-outline-primary btn-sm">
        Detail
    </Link>
);
